import type { OrganizationSettings, UpdateOrganizationInput } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { Briefcase, Building2, Fuel, Globe, MapPin, Route, Siren, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  DispatchFields,
  FuelEconomyFields,
  SuggestionFields,
  DriversFields,
  JobsFields,
  LocaleFields,
  LocationFields,
  ParticularsFields,
  TripPlanningFields,
  type CompanySettingsFormValues,
} from './CompanySettingsFields';
import { IncidentSmsFields } from './IncidentSmsFields';
import { COMPANY_PAGE_GRID } from '../policies/company-page-layout';
import { SettingsCard } from './SettingsCard';

interface CompanySettingsFormProps {
  settings: OrganizationSettings;
  error: string | null;
  submitting: boolean;
  onSave: (input: UpdateOrganizationInput) => Promise<boolean>;
  /** The branding card: it saves on its own (each logo uploads as picked), so it sits beside the form's cards. */
  aside: ReactNode;
}

function initialValuesOf(settings: OrganizationSettings): CompanySettingsFormValues {
  return {
    name: settings.name,
    legalName: settings.legalName ?? undefined,
    kraPin: settings.kraPin ?? undefined,
    address: settings.address ?? undefined,
    location: settings.location,
    currency: settings.currency,
    timezone: settings.timezone,
    dateFormat: settings.dateFormat,
    language: settings.language,
    countryCode: settings.countryCode ?? '',
    jobDueSoonThresholdDays: settings.jobDueSoonThresholdDays,
    creditOverdueThresholdDays: settings.creditOverdueThresholdDays,
    driverRestHours: settings.driverRestHours,
    notificationSmsFallbackMinutes: settings.notificationSmsFallbackMinutes,
    incidentSmsNumber: settings.incidentSmsNumber ?? undefined,
    incidentSmsWaitMinutes: settings.incidentSmsWaitMinutes,
    serviceDueSoonKm: settings.serviceDueSoonKm,
    serviceDueSoonDays: settings.serviceDueSoonDays,
    capacityWarningPercent: settings.capacityWarningPercent,
    dispatchBlocksUnreconciledCash: settings.dispatchBlocksUnreconciledCash,
    suggestionWeights: settings.suggestionWeights,
    suggestionPageSize: settings.suggestionPageSize,
    fuelWindowTrips: settings.fuelWindowTrips,
    fuelMinSamples: settings.fuelMinSamples,
    fuelOutlierPercent: settings.fuelOutlierPercent,
    tripDrivingHoursPerDay: settings.tripDrivingHoursPerDay,
    tripRestHoursPerDay: settings.tripRestHoursPerDay,
  };
}

function updateInputOf(values: CompanySettingsFormValues): UpdateOrganizationInput {
  return {
    name: values.name,
    legalName: values.legalName || undefined,
    kraPin: values.kraPin || undefined,
    address: values.address || undefined,
    location: values.location ?? null,
    countryCode: values.countryCode || undefined,
    currency: values.currency,
    timezone: values.timezone,
    dateFormat: values.dateFormat,
    language: values.language,
    jobDueSoonThresholdDays: values.jobDueSoonThresholdDays,
    creditOverdueThresholdDays: values.creditOverdueThresholdDays,
    driverRestHours: values.driverRestHours,
    notificationSmsFallbackMinutes: values.notificationSmsFallbackMinutes,
    incidentSmsNumber: values.incidentSmsNumber || null,
    incidentSmsWaitMinutes: values.incidentSmsWaitMinutes,
    serviceDueSoonKm: values.serviceDueSoonKm,
    serviceDueSoonDays: values.serviceDueSoonDays,
    capacityWarningPercent: values.capacityWarningPercent,
    dispatchBlocksUnreconciledCash: values.dispatchBlocksUnreconciledCash,
    suggestionWeights: values.suggestionWeights,
    suggestionPageSize: values.suggestionPageSize,
    fuelWindowTrips: values.fuelWindowTrips,
    fuelMinSamples: values.fuelMinSamples,
    fuelOutlierPercent: values.fuelOutlierPercent,
    tripDrivingHoursPerDay: values.tripDrivingHoursPerDay,
    tripRestHoursPerDay: values.tripRestHoursPerDay,
  };
}

/**
 * The company page as one form in two columns, so it fits a screen rather than scrolling: particulars and
 * locale with the save on the left, the location map and the branding on the right. One save covers every
 * card but branding, whose logos upload as they are picked. Stacked on narrower screens.
 */
export function CompanySettingsForm({ settings, error, submitting, onSave, aside }: CompanySettingsFormProps) {
  const [form] = Form.useForm<CompanySettingsFormValues>();

  const submit = (values: CompanySettingsFormValues) => onSave(updateInputOf(values));

  return (
    <Form<CompanySettingsFormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      disabled={submitting}
      onFinish={submit}
      initialValues={initialValuesOf(settings)}
      className={COMPANY_PAGE_GRID}
    >
      <div className="flex min-w-0 flex-col gap-6">
        {error && <Alert type="error" title={error} showIcon />}
        <SettingsCard icon={Building2} title="Company particulars">
          <ParticularsFields />
        </SettingsCard>
        <SettingsCard icon={Globe} title="Locale">
          <LocaleFields />
        </SettingsCard>
        <SettingsCard icon={Briefcase} title="Jobs">
          <JobsFields />
        </SettingsCard>
        <SettingsCard icon={Truck} title="Drivers">
          <DriversFields />
        </SettingsCard>
        <SettingsCard icon={Siren} title="Incident texts from the driver's phone">
          <IncidentSmsFields />
        </SettingsCard>
        <SettingsCard icon={Truck} title="Dispatch warnings">
          <DispatchFields />
        </SettingsCard>
        <SettingsCard icon={Fuel} title="Fuel consumption">
          <FuelEconomyFields />
        </SettingsCard>
        <SettingsCard icon={Route} title="Trip planning">
          <TripPlanningFields />
        </SettingsCard>
        <SettingsCard icon={Truck} title="Assignment suggestions">
          <SuggestionFields />
        </SettingsCard>
        <div>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save changes
          </Button>
        </div>
      </div>
      <div className="flex min-w-0 flex-col gap-6">
        <SettingsCard icon={MapPin} title="Location">
          <LocationFields />
        </SettingsCard>
        {aside}
      </div>
    </Form>
  );
}
